import styled from "styled-components";

const CONTACT_EMAIL = "support@biterecipes.co";

function Terms() {
  return (
    <Container>
      <Section>
        <PageTitle>Terms of Service for Bite</PageTitle>
        <Body>
          <div>
            <strong>Effective Date:</strong> October 18, 2026
          </div>
          <div>
            Welcome to Bite! These Terms of Service (“Terms”) apply to your use
            of the Bite mobile app, the website at biterecipes.co, the Bite
            connector for AI assistants, and related services (together,
            “Bite”). By creating an account or using Bite,
            you agree to these Terms and to our{" "}
            <a href="/privacy">Privacy Policy</a>. If you do not agree, do not
            use Bite.
          </div>
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>Who Can Use Bite</Title>
        <Body>
          You must be at least 13 years old to use Bite. If you are under the
          age of majority where you live, you may use Bite only with the
          permission of a parent or guardian who agrees to these Terms.
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>Your Account</Title>
        <Body>
          <ul>
            <li>
              <strong>Accurate information:</strong> You are responsible for
              keeping the information in your account accurate and complete.
            </li>
            <li>
              <strong>Account security:</strong> You are responsible for
              keeping your login credentials confidential and for activity that
              happens under your account. Tell us promptly at {CONTACT_EMAIL}{" "}
              if you believe your account has been accessed without your
              permission.
            </li>
            <li>
              <strong>Deleting your account:</strong> You can stop using Bite
              at any time and ask us to delete your account. See{" "}
              <a href="/delete-account">Delete Your Account</a> for how.
            </li>
          </ul>
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>Your Content</Title>
        <Body>
          <ul>
            <li>
              <strong>Ownership:</strong> You keep ownership of the recipes,
              photos, collections, notes, comments and other content you add to
              Bite (“User Content”).
            </li>
            <li>
              <strong>License to Bite:</strong> You grant Bite a non-exclusive,
              royalty-free, worldwide license to host, store, reproduce,
              format, display and distribute your User Content as needed to
              operate, provide and improve Bite. For content you make public,
              this includes showing it to other users and making it available
              through share links. This license ends when you delete the
              content or your account, except for copies other users have
              already saved or shared, backups kept for a limited time, and
              content we must keep for legal reasons.
            </li>
            <li>
              <strong>Your responsibility:</strong> You are responsible for
              your User Content and confirm that you have the rights needed to
              post it and to grant the license above.
            </li>
          </ul>
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>Recipes You Import</Title>
        <Body>
          <div>
            Bite lets you save recipes from Instagram, TikTok, websites, photos
            and other sources. Recipes, photos and videos you import usually
            belong to the person who created them.
          </div>
          <ul>
            <li>
              Imported recipes are for your personal use, such as saving,
              organizing and cooking from them.
            </li>
            <li>
              Do not publicly share another person’s recipe text, photos or
              videos on Bite, or present them as your own, unless you have
              permission or the law otherwise allows it.
            </li>
            <li>
              Where possible, Bite keeps a link to the original source. Please
              keep that credit intact.
            </li>
          </ul>
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>Community Guidelines</Title>
        <Body>
          <div>
            Bite is a place to share food and cooking. We have zero tolerance
            for objectionable content or abusive users. You agree not to post,
            share or send content, including recipes, photos, profiles, reviews
            and replies, that:
          </div>
          <ul>
            <li>
              Is illegal, or promotes illegal activity or dangerous behavior.
            </li>
            <li>
              Harasses, bullies, threatens or intimidates anyone, or promotes
              hate or discrimination based on race, ethnicity, national origin,
              religion, gender, sexual orientation, disability or any other
              protected characteristic.
            </li>
            <li>
              Is sexually explicit or pornographic, or sexualizes minors in any
              way.
            </li>
            <li>Is graphically violent or promotes self-harm.</li>
            <li>
              Infringes someone else’s copyright, trademark, privacy or other
              rights.
            </li>
            <li>
              Is spam, a scam, misleading, or impersonates another person or
              brand.
            </li>
            <li>
              Makes false or dangerous health claims, or shares food safety
              practices likely to cause harm.
            </li>
          </ul>
          <div>
            You also agree not to use Bite for illegal purposes, to harm or
            attempt to harm others, to collect other users’ information without
            permission, or to interfere with how Bite works, including by
            attempting to breach its security or accessing it with automated
            tools we have not authorized. Using the Bite connector through a
            supported AI assistant, as described in “Connecting AI Assistants”
            below, is authorized.
          </div>
          <div>
            We use automated screening, including artificial intelligence, to
            review photos you upload and usernames you choose. Content that
            does not meet these guidelines may be rejected or removed.
          </div>
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>Reporting Content and Users</Title>
        <Body>
          <ul>
            <li>
              <strong>How to report:</strong> If you see content or a user that
              breaks these Terms, email {CONTACT_EMAIL} with a link to or
              description of the content or account.
            </li>
            <li>
              <strong>What we do:</strong> We review reports promptly. We may
              remove content, restrict features, or suspend or terminate
              accounts that violate these Terms, with or without notice.
            </li>
          </ul>
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>Copyright Complaints</Title>
        <Body>
          <div>
            We respect intellectual property rights and respond to notices of
            alleged copyright infringement consistent with the U.S. Digital
            Millennium Copyright Act (DMCA). If you believe content on Bite
            infringes your copyright, send a notice to {CONTACT_EMAIL} that
            includes:
          </div>
          <ul>
            <li>
              Your physical or electronic signature and your contact
              information.
            </li>
            <li>A description of the copyrighted work you believe was infringed.</li>
            <li>
              A link to, or enough detail to locate, the content you believe is
              infringing.
            </li>
            <li>
              A statement that you have a good-faith belief that the use is not
              authorized by the copyright owner, its agent or the law.
            </li>
            <li>
              A statement, under penalty of perjury, that the information in
              your notice is accurate and that you are the copyright owner or
              authorized to act on the owner’s behalf.
            </li>
          </ul>
          <div>
            If your content was removed and you believe that was a mistake, you
            may send a counter-notice to the same address. We terminate the
            accounts of repeat infringers in appropriate circumstances.
          </div>
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>Recipes, Nutrition and AI Features</Title>
        <Body>
          <div>
            Some Bite features use artificial intelligence to import and format
            recipes, answer questions about recipes, and generate recipe
            images, and you can use Bite from AI assistants you connect. These
            results, including meal plans, shopping lists and recipes created
            or changed by an AI assistant, can be incomplete or wrong, and
            generated images may not look like the finished dish.
          </div>
          <ul>
            <li>
              Always check ingredients, quantities, cooking times and
              temperatures before relying on them.
            </li>
            <li>
              Check ingredients carefully for allergens and dietary needs.
              Bite cannot guarantee that a recipe is safe for your allergies,
              intolerances or health conditions.
            </li>
            <li>
              Recipes, nutrition information and answers on Bite are for
              general information only and are not medical, nutritional or
              dietary advice.
            </li>
          </ul>
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>Connecting AI Assistants</Title>
        <Body>
          <div>
            Bite offers a connector that lets you use your Bite account from AI
            assistants such as Claude and ChatGPT.
          </div>
          <ul>
            <li>
              <strong>Free and Pro:</strong> Anyone with a Bite account can
              connect an assistant and use it with their recipes and
              collections. Meal planning and shopping lists through the
              connector need an active Bite Pro subscription, as they do in the
              app.
            </li>
            <li>
              <strong>Acting for you:</strong> When you connect an assistant,
              you allow it to read and update your Bite account as described on
              our <a href="/connect">connector page</a> and in our{" "}
              <a href="/privacy">Privacy Policy</a>. Actions your assistant
              takes through the connector count as actions you take, and these
              Terms, including the Community Guidelines and the rules for
              recipes you import, apply to them. Check what your assistant
              plans to do, especially before it changes several things at once.
            </li>
            <li>
              <strong>Limits:</strong> The connector cannot delete content,
              edit other people’s recipes, change a recipe’s privacy, post
              reviews or follow people. Recipes saved through it are private,
              and recipes without an approved photo are saved as drafts. If it
              edits one of your recipes that is already public, the change is
              visible to others, as it would be if you made it in the app. Your usual Bite limits apply, along with rate
              limits that keep the service reliable.
            </li>
            <li>
              <strong>The assistant is not Bite:</strong> AI assistants are
              provided by other companies under their own terms and privacy
              policies. We don’t control them and aren’t responsible for what
              they say or do, including meal plans, shopping lists, recipes,
              quantities or substitutions they suggest. Treat that output the
              way you would any AI-generated recipe content (see “Recipes,
              Nutrition and AI Features” above).
            </li>
            <li>
              <strong>Fair use:</strong> Use the connector for your own cooking
              and recipe management. Don’t use it to copy other people’s
              content in bulk, to get around Bite’s limits or security, or to
              give anyone else access to your account.
            </li>
            <li>
              <strong>Disconnecting:</strong> You can disconnect at any time
              from{" "}
              <a href="/app/settings/connected-apps">
                Settings → Connected apps
              </a>{" "}
              on biterecipes.co. We may limit, suspend or end connector access,
              or change or discontinue the connector, for example to protect
              your account or Bite.
            </li>
          </ul>
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>Subscriptions and Purchases</Title>
        <Body>
          <div>
            Some features, such as meal planning and shopping lists (in the app
            and through connected AI assistants), require a paid subscription
            or purchase. Purchases
            are made through the Apple App Store or Google Play and are billed
            to your store account.
          </div>
          <ul>
            <li>
              <strong>Automatic renewal:</strong> Subscriptions renew
              automatically at the end of each billing period unless you cancel
              at least 24 hours before the current period ends.
            </li>
            <li>
              <strong>Cancelling:</strong> You can manage or cancel a
              subscription in your App Store or Google Play account settings.
              Deleting the app or your Bite account does not cancel a
              subscription.
            </li>
            <li>
              <strong>Free trials:</strong> If a subscription includes a free
              trial, it converts to a paid subscription when the trial ends
              unless you cancel before then.
            </li>
            <li>
              <strong>Refunds:</strong> Refunds are handled by Apple or Google
              under their policies.
            </li>
            <li>
              <strong>Price changes:</strong> If a subscription price changes,
              you will be notified in advance as required by the App Store or
              Google Play.
            </li>
          </ul>
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>Ads and Sponsored Content</Title>
        <Body>
          Bite may display ads, sponsored content, sponsored recipe previews or
          promotional links. Sponsored content may look similar to other recipe
          or discovery content, but we aim to label it clearly when it is an ad
          or sponsored placement.
          <ul>
            <li>
              Ads or sponsored items may link to websites, apps, products or
              services operated by third parties.
            </li>
            <li>
              Bite is not responsible for third-party advertiser content,
              products, services, websites, privacy practices or terms.
            </li>
            <li>
              Your interactions with advertisers or third-party links are
              solely between you and the applicable third party.
            </li>
          </ul>
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>Third-Party Services</Title>
        <Body>
          Bite relies on third-party services, including Google Firebase,
          Google Gemini, Google advertising services, Meta, the Apple App
          Store and Google Play. If you connect an AI assistant, such as
          Anthropic’s Claude or OpenAI’s ChatGPT, you use that assistant under
          its provider’s terms.
          Your use of those services may be subject to their own terms and
          privacy policies.
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>Bite’s Intellectual Property</Title>
        <Body>
          The Bite app, website, name, logo, design and content (other than
          User Content and content owned by others) belong to Bite. You may not
          copy, modify, distribute or reverse engineer any part of Bite without
          our permission, except as the law allows.
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>Suspension and Termination</Title>
        <Body>
          We may suspend or terminate your account or access to Bite if you
          violate these Terms, if required by law, or if your use creates risk
          or harm for Bite or other users. You may stop using Bite and delete
          your account at any time.
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>Disclaimers and Limitation of Liability</Title>
        <Body>
          <div>
            Bite is provided “as is” and “as available” without warranties of
            any kind, to the fullest extent permitted by law. We do not promise
            that Bite will be uninterrupted, error-free or that content on it
            will be accurate.
          </div>
          <div>
            To the fullest extent permitted by law, Bite is not liable for any
            indirect, incidental, special, consequential or punitive damages,
            or for any loss of data, arising from your use of Bite, including
            content posted by other users and actions or output of AI
            assistants you connect to Bite. Nothing in these Terms limits
            liability that cannot be limited under applicable law.
          </div>
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>Apple App Store Terms</Title>
        <Body>
          <div>If you downloaded Bite from the Apple App Store:</div>
          <ul>
            <li>
              These Terms are between you and Bite, not Apple. Bite, not Apple,
              is solely responsible for the app and its content.
            </li>
            <li>
              Your license to use the app is limited to use on Apple-branded
              devices you own or control, as permitted by the Usage Rules in
              the App Store Terms of Service.
            </li>
            <li>
              Apple has no obligation to provide maintenance or support for the
              app.
            </li>
            <li>
              If the app fails to conform to any applicable warranty, you may
              notify Apple, and Apple will refund the purchase price, if any.
              To the maximum extent permitted by law, Apple has no other
              warranty obligation with respect to the app.
            </li>
            <li>
              Bite, not Apple, is responsible for addressing any claims
              relating to the app, including product liability claims, claims
              that the app fails to meet legal or regulatory requirements, and
              consumer protection claims.
            </li>
            <li>
              If a third party claims that the app or your use of it infringes
              their intellectual property rights, Bite, not Apple, is
              responsible for the investigation, defense, settlement and
              discharge of that claim.
            </li>
            <li>
              You confirm that you are not located in a country subject to a
              U.S. Government embargo or designated as a “terrorist
              supporting” country, and that you are not on any U.S. Government
              list of prohibited or restricted parties.
            </li>
            <li>
              Apple and its subsidiaries are third-party beneficiaries of these
              Terms and may enforce them against you.
            </li>
          </ul>
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>Changes to These Terms</Title>
        <Body>
          We may update these Terms from time to time. We will post the updated
          Terms on this page and update the effective date, and we will notify
          you in the app or by email before material changes take effect.
          Continuing to use Bite after the updated Terms take effect means you
          accept them.
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>Governing Law and Disputes</Title>
        <Body>
          These Terms are governed by the laws of the State of New York, United
          States, without regard to its conflict of law provisions. Any
          disputes arising from these Terms or your use of Bite will be
          resolved exclusively in the state or federal courts located in New
          York, United States.
        </Body>
      </Section>
      <Divider />
      <Section>
        <Title>Contact Us</Title>
        <Body>
          <div>
            If you have any questions about these Terms, please contact us at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </div>
        </Body>
      </Section>
    </Container>
  );
}

export default Terms;

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding: 60px;
  padding-top: 0px;
  @media (max-width: 590px) {
    padding: 30px;
    padding-top: 0px;
  }
`;

const Section = styled.section`
  padding-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Divider = styled.div`
  width: 100%;
  height: 0px;
  border-top: 0.5px solid black;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 35px;
  font-weight: 800;
  font-family: sans-serif;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 25px;
  font-weight: 800;
  font-family: sans-serif;
`;

const Body = styled.div`
  font-size: 18px;
  font-weight: 500;
  font-family: sans-serif;
  line-height: 1.6;
  display: flex;
  flex-direction: column;
  gap: 10px;

  strong {
    font-weight: 600;
  }

  a {
    color: inherit;
  }

  ul {
    margin-top: 10px;
    padding-left: 20px;

    li {
      margin-bottom: 8px;
    }
  }
`;
